import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Button,
    Heading,
    Image,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Text,
    VStack,
    useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
// import Compressor from 'compressorjs';
import { dungeonEvent } from "../../hardcoded/events";
import Gallery from "../utils/Gallery";
import { PageTitle } from "../utils/PageTitle";

type Photo = {
    id: number;
    src: string;
    alt: string;
};

const photos: Photo[] = [
    { id: 1, src: "/images/dungeon/img1.jpg", alt: "A group photo with all participants and organizers of GDD 2024." },
    { id: 2, src: "/images/dungeon/img2.jpg", alt: "Hard game design work." },
    { id: 3, src: "/images/dungeon/dungeon-heading-2.jpg", alt: "Praise the Sun." },
    { id: 4, src: "/images/dungeon/img3.jpg", alt: "Just chilling with a controller." },
    { id: 5, src: "/images/dungeon/img4.jpg", alt: "Paying attention to a talk." },
    { id: 6, src: "/images/dungeon/img5.jpg", alt: "Game jam in progress." },
    { id: 7, src: "/images/dungeon/img6.jpg", alt: "Social desocializing." },
    { id: 8, src: "/images/dungeon/img7.jpg", alt: "Experts sharing their wisdom." },
    { id: 9, src: "/images/dungeon/img8.jpg", alt: "We go outdoors too." },
];

export function Dungeon() {
    const { t } = useTranslation();

    const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = (photo: Photo) => {
        setSelectedPhoto(photo);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedPhoto(null);
        setIsModalOpen(false);
    };

    return (
        <Box>
            <Box position="relative" w="100%">
                <Image
                    src="/images/dungeon/dungeon-heading-3.jpg"
                    alt="heading-dungeon"
                    w="100%"
                    h={{ base: 36, sm: 36, md: 56, lg: 72 }}
                    objectFit="cover"
                    objectPosition={"center center"}
                    shadow={"md"}
                />

                {/* original top={7} */}
                <Breadcrumb position="absolute" top={7} left={10} color={"white"}>
                    <BreadcrumbItem>
                        <BreadcrumbLink href="/">Garrigue Games</BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbItem>
                        <BreadcrumbLink href="/courses">{t("menuButtons.courses")}</BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbItem fontWeight={"semibold"} isCurrentPage>
                        <BreadcrumbLink href="/courses/dungeon">{t("dungeon.title")}</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Box>

            <VStack maxW={1536} p={10} alignItems={"left"}>
                <PageTitle>{t("dungeon.title")}</PageTitle>
                <Text marginBottom={5} fontSize="md">
                    {t("dungeon.paragraph1")}
                </Text>
                <Text fontSize="md">
                    {t("dungeon.prebutton")}
                    <Text textStyle="link" as="span">
                        <Link
                            href="https://docs.google.com/spreadsheets/d/15Mo2JQGw4v_DKs_7QAVK1-28c_VddpxCrLRwAmNtXac/edit?usp=sharing"
                            isExternal
                        >
                            {" "}
                            Harmonogram 2024.
                        </Link>
                    </Text>
                </Text>
                <Text fontSize="md">{t("dungeon.paragraph2")}</Text>
                <Text marginBottom={5} fontSize="md">
                    {t("dungeon.paragraph3")}
                </Text>

                <Link href="https://is.muni.cz/auth/predmet/fi/VV078" isExternal>
                    <Button variant="outline" size="md" marginBottom={5}>
                        {t("dungeon.syllabus")}
                    </Button>
                </Link>
                <Heading fontSize="2xl" fontWeight={"medium"} color={useColorModeValue("brand.500", "brand.400")}>
                    {t("dungeon.basicinfoheading")}
                </Heading>
                <Text fontWeight="semibold">
                    {t("dungeon.date")}
                    <Text as="span" fontWeight="normal">
                        {dungeonEvent.from.format("D. M. YYYY") + " - " + dungeonEvent.to.format("D. M. YYYY")}
                    </Text>
                </Text>
                <Text fontWeight="semibold">
                    {t("dungeon.place")}
                    <Text as="span" fontWeight="normal">
                        {t("dungeon.basicinfoplace")}{" "}
                        <Link href="http://navysluni-dm.cz/" isExternal>
                            (http://navysluni-dm.cz/)
                        </Link>
                    </Text>
                </Text>
                <Text fontWeight="semibold">
                    {t("dungeon.transport")}
                    <Text as="span" fontWeight="normal">
                        {t("dungeon.basicinfotransport")}
                    </Text>
                </Text>
                <Text fontWeight="semibold" marginBottom={5}>
                    {t("dungeon.costs")}
                    <Text as="span" fontWeight="normal">
                        {t("dungeon.basicinfocosts")}
                    </Text>
                </Text>

                <Heading fontSize="2xl" fontWeight={"medium"} color={useColorModeValue("brand.500", "brand.400")}>
                    {t("dungeon.photosheading")}
                </Heading>

                <Gallery photos={photos} onPhotoClick={openModal} />
            </VStack>

            <Modal isOpen={isModalOpen} onClose={closeModal} size={"3xl"} isCentered>
                <ModalOverlay />
                <ModalContent m={0} borderRadius="none">
                    <ModalCloseButton
                        size="lg"
                        _hover={{ color: "black" }}
                        zIndex="1"
                        position="absolute"
                        top="4"
                        right="4"
                    />
                    <ModalBody p={0}>
                        {selectedPhoto && (
                            <Image
                                key={selectedPhoto?.id}
                                src={selectedPhoto?.src}
                                alt={selectedPhoto?.alt}
                                boxSize="100%"
                                width="100vw"
                                height="100%"
                                objectFit="cover"
                                objectPosition="center"
                                shadow="md"
                                cursor="pointer"
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default Dungeon;
